import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/authContext";
import { setActiveArtistId } from "@/api/client";
import { claimInvite, validateInvite } from "@/api/invites";
import { fetchCurrentAccount } from "@/api/account";
import type { InviteValidateResponse } from "@/api/invites";

export default function ClaimInvitePage() {
  const navigate = useNavigate();
  const { completeLogin } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [invite, setInvite] = useState<InviteValidateResponse | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidationError("Missing invite token.");
      setInvite(null);
      return;
    }
    setValidating(true);
    setValidationError(null);
    void validateInvite(token)
      .then((response) => {
        setInvite(response);
        if (!response.valid) {
          setValidationError("This invite is no longer valid.");
        }
      })
      .catch(() => {
        setValidationError("Unable to validate the invite token.");
        setInvite(null);
      })
      .finally(() => {
        setValidating(false);
      });
  }, [token]);

  const inviteEmail = invite?.email ?? "Unknown recipient";
  const inviteRole = invite?.role ?? "member";
  const canSubmit =
    Boolean(token) &&
    Boolean(invite?.valid) &&
    Boolean(firstName.trim()) &&
    Boolean(lastName.trim()) &&
    Boolean(password) &&
    Boolean(confirmPassword) &&
    password === confirmPassword;

  const validationMessage = useMemo(() => {
    if (validationError) return validationError;
    if (!token) return "No invite token provided.";
    if (validating) return "Validating invite…";
    if (invite && !invite.valid) return "Invite expired or already used.";
    if (invite?.valid) return `Invite for ${inviteEmail} (${inviteRole}).`;
    return null;
  }, [invite, inviteEmail, inviteRole, validating, validationError, token]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) {
      setFormError("Please complete all fields and ensure passwords match.");
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();
      const response = await claimInvite({
        token,
        first_name: trimmedFirstName,
        last_name: trimmedLastName,
        password,
      });
      const sessionPayload = {
        user: {
          id: response.user_profile.id,
          email: response.user_profile.email,
          name: response.user_profile.name,
          permissions: [],
          role: "owner",
        },
        organization: response.organization,
        permissions: [],
        teams: [],
        capabilities: {},
        onboarding_status: response.onboarding_status,
      };
      await completeLogin(response.access_token, sessionPayload);
      try {
        const accountProfile = await fetchCurrentAccount();
        await completeLogin(response.access_token, { ...sessionPayload, ...accountProfile });
      } catch {
        // Best-effort refresh; onboarding can proceed without it.
      }
      setActiveArtistId("");
      navigate("/signup/account", {
        replace: true,
        state: {
          prefillAccount: {
            email: response.user_profile.email,
            first_name: trimmedFirstName,
            last_name: trimmedLastName,
          },
        },
      });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Unable to claim invite.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="launch-root flex min-h-screen items-center justify-center px-4 text-foreground">
      <div className="launch-hero-bg" aria-hidden="true" />
      <div className="launch-orb launch-orb--one" aria-hidden="true" />
      <div className="launch-orb launch-orb--two" aria-hidden="true" />
      <div className="aa-glass-card relative z-10 w-full max-w-md space-y-6 rounded-2xl p-8 shadow-xl">
        <header>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Invite claim</p>
          <h1 className="aa-page-title mt-1 text-2xl font-semibold">
            <span className="aa-gradient-text">Accept your invitation</span>
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Use the invite token you received to create your account and continue to onboarding.
          </p>
        </header>
        {validationMessage && (
          <p className="rounded-md border border-border/70 bg-background/50 px-3 py-2 text-sm text-foreground/80">
            {validationMessage}
          </p>
        )}
        {!token && <p className="text-sm text-rose-400">No invite token was provided in the URL.</p>}
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <Label>Email address</Label>
            <Input value={inviteEmail} disabled className="cursor-not-allowed bg-background/50" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>First name</Label>
              <Input value={firstName} onChange={(event) => setFirstName(event.target.value)} required />
            </div>
            <div>
              <Label>Last name</Label>
              <Input value={lastName} onChange={(event) => setLastName(event.target.value)} required />
            </div>
          </div>
          <div>
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </div>
          <div>
            <Label>Confirm password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
            />
          </div>
          {formError && <p className="text-xs text-rose-400">{formError}</p>}
          <Button type="submit" className="w-full" disabled={!canSubmit || submitting}>
            {submitting ? "Claiming…" : "Claim invite & continue"}
          </Button>
        </form>
      </div>
    </div>
  );
}
